import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccessDirectory } from "@shared/access/directory";
import { DEMO_COMPANY_NAME } from "@shared/demo";
import type { HomeChoice } from "@shared/access/userAccess";
import type { ContentOwnerRecord } from "../../http/foyer";

const COMPANY_TYPES = [
  "CUSTOMER",
  "ADVERTISER",
  "RESELLER",
  "SYSTEM_INTEGRATOR",
  "CONTENT_OWNER",
] as const;

type CompanyType = (typeof COMPANY_TYPES)[number];

const isCompanyType = (value: string): value is CompanyType =>
  (COMPANY_TYPES as readonly string[]).includes(value);

export interface HomePickerProps {
  value: HomeChoice | null;
  onChange: (home: HomeChoice) => void;
  directory: AccessDirectory;
  owners: readonly ContentOwnerRecord[];
  /** Only a Nenda admin may choose Nenda. */
  canChooseNenda: boolean;
  /** Who is being given the home, for the confirmation. */
  who: string;
  disabled?: boolean;
}

/**
 * The user's home organisation. Nenda sits apart from the customer companies
 * and is confirmed, so it cannot be mistaken for a company such as "Nenda Demo".
 */
export function HomePicker({
  value,
  onChange,
  directory,
  owners,
  canChooseNenda,
  who,
  disabled = false,
}: HomePickerProps) {
  const { t } = useTranslation(["users", "common"]);
  const [confirming, setConfirming] = useState(false);
  const isNenda = value?.kind === "nenda";
  const companies = directory.companies.filter(
    (c) => c.id !== directory.nendaCompanyId
  );
  const showNenda = canChooseNenda || isNenda;

  return (
    <Stack spacing={1} data-testid="home-picker">
      <Typography variant="subtitle2">{t("homePicker.title")}</Typography>
      {showNenda && (
        <Box
          role="radio"
          aria-checked={isNenda}
          aria-disabled={disabled || !canChooseNenda}
          tabIndex={disabled || !canChooseNenda ? -1 : 0}
          data-testid="home-nenda"
          onClick={() => {
            if (!disabled && canChooseNenda && !isNenda) setConfirming(true);
          }}
          onKeyDown={(e) => {
            if (
              (e.key === "Enter" || e.key === " ") &&
              !disabled &&
              canChooseNenda &&
              !isNenda
            ) {
              e.preventDefault();
              setConfirming(true);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.25,
            borderRadius: 2,
            border: "2px dashed",
            borderColor: isNenda ? "primary.main" : "divider",
            bgcolor: isNenda ? "action.selected" : "transparent",
            cursor: disabled || !canChooseNenda ? "default" : "pointer",
          }}
        >
          <Radio
            checked={isNenda}
            disabled={disabled || !canChooseNenda}
            tabIndex={-1}
            size="small"
            inputProps={{ "aria-hidden": true }}
          />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "fontWeightBold" }}>
              {t("homePicker.nendaStaff")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("homePicker.nendaStaffHelp")}
            </Typography>
          </Box>
        </Box>
      )}
      <TextField
        select
        label={t("homePicker.companyLabel")}
        value={value?.kind === "company" ? value.companyId : ""}
        onChange={(e) =>
          onChange({ kind: "company", companyId: e.target.value })
        }
        size="small"
        disabled={disabled}
        inputProps={{ "data-testid": "home-company" }}
      >
        {companies.map((c) => {
          const owner = owners.find((o) => o.id === c.id);
          const type = isCompanyType(c.type)
            ? t(`homePicker.companyTypes.${c.type}`)
            : c.type;
          return (
            <MenuItem key={c.id} value={c.id}>
              {owner && !owner.enabled
                ? t("homePicker.companyOptionDisabled", { name: c.name, type })
                : t("homePicker.companyOption", { name: c.name, type })}
            </MenuItem>
          );
        })}
      </TextField>
      {value === null && (
        <Alert severity="warning" sx={{ py: 0 }} data-testid="home-missing">
          {t("homePicker.missing")}
        </Alert>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>{t("homePicker.confirmTitle", { who })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("homePicker.confirmBody", { demo: DEMO_COMPANY_NAME })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            data-testid="home-nenda-confirm"
            onClick={() => {
              setConfirming(false);
              onChange({ kind: "nenda" });
            }}
          >
            {t("homePicker.confirmYes")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
